// Pure text/formatting/pacing helpers extracted from WatchPrayer.jsx.
// No React, no side effects, no coupling to page-level state — every
// export is a deterministic function of its inputs. Kept together in
// one file so callers (WatchPrayer, future preview panels, tests) can
// pull them from a single import path.

// ---- Metadata formatters ----

export const fmtDur = (s) => {
  const m = Math.floor((s || 0) / 60);
  const sec = Math.floor((s || 0) % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};

// Compact number formatter for metadata chips (mirrors PrayerCard so
// the same value renders consistently on the card and the watch page).
export const fmtCount = (n) => {
  const v = Number(n) || 0;
  if (v < 1000) return String(v);
  if (v < 10000) return `${(v / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  if (v < 1_000_000) return `${Math.round(v / 1000)}k`;
  if (v < 10_000_000) return `${(v / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  return `${Math.round(v / 1_000_000)}M`;
};

export const fmtLongDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return new Date(iso).toLocaleDateString();
  }
};

// ---- Tokenizer ----
// Tokenize the description into an ordered list of tokens where each
// token is either a word (with a global word index) or a
// "linebreak" / "paragraph" marker. This lets us render individual
// word <span>s for highlighting while preserving the original poem
// layout.
//
// Tokens starting with `@` are treated as user-handle mentions: they
// still render on screen as regular text so the reader sees who the
// prayer names, but they are given NO `wordIndex` and are NOT counted
// in `wordCount`. This keeps the word-by-word highlighter perfectly
// in sync with the system voice narration — which strips @handles
// before synthesis (see `prayer_tts` in the backend). Without this
// exclusion the highlight would drift ahead of the audio, one word
// per handle.
export const tokenize = (text) => {
  if (!text) return { tokens: [], wordCount: 0 };
  const tokens = [];
  let wordIndex = 0;
  const paragraphs = text.split(/\n{2,}/);
  paragraphs.forEach((para, pi) => {
    if (pi > 0) tokens.push({ type: 'para' });
    const lines = para.split('\n');
    lines.forEach((line, li) => {
      if (li > 0) tokens.push({ type: 'br' });
      const words = line.split(/\s+/).filter(Boolean);
      words.forEach((w, i) => {
        // Skip highlighting for any token that is purely a @handle
        // (with optional surrounding punctuation like `(@a)` or
        // `@mark,`). The TTS backend strips these before synthesis,
        // so the audio never pauses on them. Anything with letters
        // left after stripping is still narrated, so it keeps its
        // wordIndex.
        const speakable = w.replace(/@\w+/g, '');
        const hasSpeakableLetters = /[A-Za-z0-9]/.test(speakable);
        if (!hasSpeakableLetters) {
          tokens.push({ type: 'word', text: w, wordIndex: null });
        } else {
          tokens.push({ type: 'word', text: w, wordIndex });
          wordIndex += 1;
        }
        if (i < words.length - 1) tokens.push({ type: 'space' });
      });
    });
  });
  return { tokens, wordCount: wordIndex };
};

// ---- Pace-profile helpers ----
// Text-derived meditative pacing helpers for the silent-highlight
// narration mode. The system infers a per-prayer rhythm from the text
// itself — no user-facing WPM knob, no LLM call, no runtime cost
// after the initial memoized pass.
//
//   syllableCount(word)   — vowel-cluster heuristic. Good enough for
//                            pacing (small errors have no perceptible
//                            effect on the highlight cadence).
//   stripEdges(word)      — strip surrounding quotes / brackets /
//                            stray punctuation so linguistic signals
//                            read cleanly.
//   isSacredWord(word)    — reverent nouns that traditionally get
//                            held a touch longer in liturgical
//                            reading.

const SACRED_WORDS = new Set([
  'lord', 'god', 'jesus', 'christ', 'spirit', 'father', 'amen',
  'hallelujah', 'alleluia', 'praise', 'glory', 'holy', 'yhwh', 'yahweh',
  'allah', 'buddha', 'krishna', 'shiva', 'rama',
]);

export const stripEdges = (w) => {
  if (!w) return '';
  return String(w)
    .replace(/^[^A-Za-z0-9]+/, '')
    .replace(/[^A-Za-z0-9']+$/, '');
};

export const syllableCount = (rawWord) => {
  const w = stripEdges(rawWord).toLowerCase();
  if (!w) return 1;
  if (w.length <= 3) return 1;
  // Silent trailing 'e' does not form its own syllable ("gratitude" →
  // "gratitud" → grat-i-tud → 3, not 4).
  const trimmed = w.replace(/e$/, '');
  const groups = trimmed.match(/[aeiouy]+/g);
  return Math.max(1, groups ? groups.length : 1);
};

export const isSacredWord = (rawWord) => {
  const clean = stripEdges(rawWord).toLowerCase();
  return clean.length > 0 && SACRED_WORDS.has(clean);
};

export const isAllCapsEmphasis = (rawWord) => {
  const clean = stripEdges(rawWord);
  if (clean.length < 3) return false;                     // "I", "A" don't count
  return clean === clean.toUpperCase() && /[A-Z]/.test(clean);
};

// Build a per-prayer pacing profile from displayText. Produces:
//   * msPerSyllable — a global tempo tuned from sentence length,
//     poetic line-break density, and vocabulary complexity.
//   * wordDelays    — the exact delay to wait AFTER highlighting each
//     word (indexed by wordIndex). Combines syllable time, sacred /
//     ALL-CAPS emphasis multipliers, line-end breath, and punctuation
//     dwell that scales proportionally with the base tempo.
//
// All values fall out of the text — no fixed WPM anywhere.
export const buildPaceProfile = (rawText) => {
  const { tokens, wordCount } = tokenize(rawText || '');
  if (wordCount === 0) {
    return { msPerSyllable: 320, wordDelays: [] };
  }

  // Pass 1 — collect per-word linguistic data and count line breaks.
  const items = [];
  let brCount = 0;
  let paraCount = 0;
  tokens.forEach((tok, i) => {
    if (tok.type === 'br') brCount += 1;
    if (tok.type === 'para') paraCount += 1;
    if (tok.type === 'word' && typeof tok.wordIndex === 'number') {
      const cleaned = stripEdges(tok.text);
      const trailing = String(tok.text).replace(/["')\]\u201D\u2019»]+$/g, '').slice(-1);
      const isPeriod = ['.', '?', '!', '…'].includes(trailing) ||
                       String(tok.text).endsWith('...') || String(tok.text).endsWith('—');
      const isComma  = [',', ';', ':'].includes(trailing);
      // Line-end: the next non-space/word token is br or para.
      let j = i + 1;
      while (j < tokens.length && tokens[j].type === 'space') j += 1;
      const lineEnd = j < tokens.length && (tokens[j].type === 'br' || tokens[j].type === 'para');
      items.push({
        idx: tok.wordIndex,
        clean: cleaned,
        syll: syllableCount(tok.text),
        sacred: isSacredWord(tok.text),
        caps: isAllCapsEmphasis(tok.text),
        endsSentence: isPeriod,
        endsClause: isComma,
        lineEnd,
      });
    }
  });

  // Pass 2 — derive global tempo from text-level signals.
  const totalSyll = items.reduce((s, it) => s + it.syll, 0);
  const sentenceCount = Math.max(1, items.filter((it) => it.endsSentence).length);
  const avgSyllPerWord = totalSyll / wordCount;
  const avgWordsPerSentence = wordCount / sentenceCount;
  const lineBreakRatio = (brCount + paraCount * 2) / wordCount;

  let msPerSyllable = 320;                                  // neutral baseline
  if (avgWordsPerSentence > 22) msPerSyllable += 35;         // long, contemplative sentences
  else if (avgWordsPerSentence > 15) msPerSyllable += 15;
  else if (avgWordsPerSentence < 7)  msPerSyllable -= 20;    // short & direct → brisker
  if (lineBreakRatio > 0.15) msPerSyllable += 30;            // poetic / verse layout
  else if (lineBreakRatio > 0.07) msPerSyllable += 15;
  if (avgSyllPerWord > 1.8) msPerSyllable += 20;             // heavy vocabulary → slower
  else if (avgSyllPerWord < 1.35) msPerSyllable -= 15;       // simple vocabulary → quicker
  // Clamp to a sane meditative range — never faster than ~fluent
  // reading, never slower than a labored crawl.
  msPerSyllable = Math.max(260, Math.min(410, msPerSyllable));

  // Punctuation dwell + line-end breath scale with the base tempo so
  // the whole rhythm feels of a piece (fast prayers get short pauses,
  // slow prayers get commensurately longer ones).
  const commaDwell   = Math.round(msPerSyllable * 0.85);
  const periodDwell  = Math.round(msPerSyllable * 1.70);
  const lineBreath   = Math.round(msPerSyllable * 0.45);

  // Envelope for a single word's own duration. Even 1-syllable words
  // get a minimum readable window; the longest words are capped so a
  // 6-syllable outlier doesn't stall the whole read.
  const minWord = Math.round(msPerSyllable * 0.9);
  const maxWord = Math.round(msPerSyllable * 3.6);

  // Pass 3 — compute the exact delay AFTER each word.
  const wordDelays = new Array(wordCount).fill(0);
  items.forEach((it) => {
    let base = it.syll * msPerSyllable;
    if (it.sacred) base *= 1.15;                             // reverent held-note
    if (it.caps)   base *= 1.25;                             // ALL-CAPS emphasis
    base = Math.max(minWord, Math.min(maxWord, base));
    if (it.endsSentence) base += periodDwell;
    else if (it.endsClause) base += commaDwell;
    if (it.lineEnd && !it.endsSentence) base += lineBreath;  // line-break breath
    wordDelays[it.idx] = Math.round(base);
  });

  return { msPerSyllable, wordDelays };
};

// Style for the prayer text container in the immersive overlay. Text
// sits directly on the dimmed background — only text color + a soft
// dark text-shadow remain (no container chrome), which keeps the copy
// legible on both image and gradient backdrops.
export const PRAYER_HIGHLIGHT_BOX_STYLE = {
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: 0,
  padding: 0,
  boxShadow: 'none',
  color: 'rgb(255, 255, 255)',
  textShadow: '0 2px 4px rgba(0,0,0,0.75), 0 0 12px rgba(0,0,0,0.55)',
};
